import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '../../../../app/components/AppButton';
import {
  IconCalendarPlus,
  IconChevronRight,
  IconClock,
  IconMore,
  IconPackage,
  IconPlus,
  IconPlusWhite,
  IconPrice,
} from '../../../../app/components/Icons';
import { useAppSelector } from '../../../../app/store/hooks';
import { lessonEditPath, paths, rolePath } from '../../../../app/routes/paths';
import { useAppRole } from '../../../../app/routes/useRolePath';
import {
  formatAmountCode,
  formatSessionCount,
} from '../../application/formatLessonMoney';
import type { LessonOffering } from '../../application/types';
import { useGetMyLessonOfferingsQuery } from '../../infrastructure/api/lessonOfferings.api';
import './lessons.scss';

function statusClass(status: string): string {
  if (status === 'published') return 'mentor-lessons__badge mentor-lessons__badge--published';
  return 'mentor-lessons__badge mentor-lessons__badge--draft';
}

function statusLabel(status: string): string {
  if (status === 'published') return 'lesson.status_published';
  if (status === 'paused') return 'lesson.status_paused';
  return 'lesson.status_draft';
}

export function LessonListPage() {
  const { t, i18n } = useTranslation();
  const role = useAppRole();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { data: offerings = [], isLoading } = useGetMyLessonOfferingsQuery(undefined, {
    skip: !accessToken,
  });
  const createHref = rolePath(role, paths.lessonCreate);

  return (
    <div className="mentor-lessons mentor-lessons--list">
      <header className="mentor-lessons__header">
        <h1>{t('lesson.my_lessons')}</h1>
        <Link className="mentor-lessons__create" to={createHref}>
          <IconPlusWhite />
          {t('lesson.create_lesson')}
        </Link>
      </header>
      {isLoading ? <p className="mentor-lessons__muted">{t('lesson.loading')}</p> : null}
      {offerings.length > 0 ? (
        <ul className="mentor-lessons__cards">
          {offerings.map((offering) => (
            <LessonCard key={offering.id} offering={offering} />
          ))}
        </ul>
      ) : null}
      {!isLoading ? (
        <div className="mentor-lessons__empty">
          <IconCalendarPlus />
          <p>{t('lesson.empty_booking')}</p>
          <LinkButton to={createHref} variant="outline">
            {t('lesson.create_lesson')}
          </LinkButton>
        </div>
      ) : null}
    </div>
  );
}

function LessonCard({ offering }: { offering: LessonOffering }) {
  const { t, i18n } = useTranslation();
  const role = useAppRole();
  const editHref = rolePath(role, lessonEditPath(offering.id));
  const description = offering.description?.trim();

  return (
    <li className="mentor-lessons__card">
      <div className="mentor-lessons__card-head">
        <div className="mentor-lessons__title-row">
          <h2>{offering.title}</h2>
          <span className={statusClass(offering.status)}>{t(statusLabel(offering.status))}</span>
        </div>
        <div className="mentor-lessons__card-actions">
          <Link to={editHref}>{t('lesson.edit')}</Link>
          <span className="mentor-lessons__more" aria-hidden>
            <IconMore />
          </span>
        </div>
      </div>
      <p className="mentor-lessons__meta">
        <span>
          <IconClock />
          {offering.durationMinutes} {t('mentor-profile.price_minutes')}
        </span>
        <span>
          <IconPrice />
          {formatAmountCode(offering.price, offering.currency, i18n.language)}
        </span>
      </p>
      {description ? <p className="mentor-lessons__description">{description}</p> : null}
      {offering.packages.length > 0 ? (
        <div className="mentor-lessons__packages">
          <h3>{t('lesson.packages')}</h3>
          <ul>
            {offering.packages.map((pkg) => (
              <li key={pkg.id}>
                <Link className="mentor-lessons__package" to={editHref}>
                  <span className="mentor-lessons__package-main">
                    <IconPackage />
                    {formatSessionCount(pkg.quantity, i18n.language)}
                    {' · '}
                    {formatAmountCode(pkg.price, pkg.currency, i18n.language)}
                    {pkg.discountPercent > 0 ? (
                      <span className="mentor-lessons__benefit">
                        {t('lesson.benefit', { percent: pkg.discountPercent })}
                      </span>
                    ) : null}
                  </span>
                  <IconChevronRight />
                </Link>
              </li>
            ))}
          </ul>
          <LinkButton
            to={editHref}
            state={{ addPackage: true }}
            variant="outline"
            icon={<IconPlus />}
          >
            {t('lesson.add_package')}
          </LinkButton>
        </div>
      ) : null}
    </li>
  );
}

export default LessonListPage;
